import { useEffect, useMemo, useState } from 'react';
import { atom, useAtomValue } from 'jotai';
import { debounce } from 'es-toolkit';
import { useTranslation } from 'react-i18next';
import { format, subHours } from 'date-fns';
import { tz } from '@date-fns/tz';
import { koKR } from '@mui/x-data-grid/locales';
import { FormControl, InputAdornment, InputLabel, MenuItem, Select, Stack, TextField, Typography } from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import { Search } from '@mui/icons-material';

import { tempUnitAtom, timeZoneAtom } from '@/interfaces/utils';
import { siteAlarmHistoryAtom } from '@/interfaces/alarm';
import { compareInLowerCase, getThresholdText } from '@/components/common/utils';
import { SettingLayout } from '@/components/setting/SettingLayout';
import DatePicker from '@/components/common/DatePicker';

import type { ChangeEvent } from 'react';
import type { SelectChangeEvent } from '@mui/material';
import type { GridColDef } from '@mui/x-data-grid';
import type { Period } from '@/@types/device';
import type { SiteAlarmHistoryData } from '@/@types/alarm';

export const siteAlarmHistoryPeriodAtom = atom<Period>({ from: subHours(new Date(), 24), to: new Date() });

export default function AlarmHistory() {
  const { data: history } = useAtomValue(siteAlarmHistoryAtom);
  const timeZone = useAtomValue(timeZoneAtom);
  const tempUnit = useAtomValue(tempUnitAtom);

  const [rows, setRows] = useState<SiteAlarmHistoryData[]>([]);
  const [query, setQuery] = useState('');
  const [alarmType, setAlarmType] = useState('all');
  const { t, i18n } = useTranslation();

  const isKorean = i18n.language.startsWith('ko');
  const locale = isKorean ? koKR.components.MuiDataGrid.defaultProps.localeText : null;
  const timeFormat = isKorean ? 'yyyy.MM.dd a hh:mm' : 'MM/dd/yyyy hh:mm a';

  const columns = useMemo<GridColDef<SiteAlarmHistoryData>[]>(
    () => [
      { field: 'serialNumber', headerName: t('Devices.label.serialNumber'), flex: 1, sortable: false },
      {
        field: 'alarmType',
        headerName: t('Devices.label.alarmType'),
        flex: 0.5,
        sortable: false,
        valueFormatter: (value: string) => t(`Devices.label.${value.toLowerCase()}`),
      },
      { field: 'message', headerName: t('Common.label.message'), flex: 2, sortable: false },
      {
        field: 'threshold',
        headerName: t('Devices.label.threshold'),
        flex: 0.5,
        sortable: false,
        valueFormatter: (_, row) => getThresholdText(row, tempUnit),
      },
      {
        field: 'timestamp',
        headerName: t('Devices.label.time'),
        flex: 1,
        sortable: true,
        type: 'dateTime',
        valueFormatter: (value: string) => format(value, timeFormat, { in: tz(timeZone) }),
      },
    ],
    [t, tempUnit, timeFormat, timeZone],
  );

  useEffect(() => {
    if (history === undefined) return;
    setAlarmType('all');
    setRows(history);
  }, [history]);

  const filterHistory = useMemo(
    () =>
      debounce((queryFilter: string, alarmTypeFilter: string) => {
        const filterResult = history.filter(
          (row) =>
            (compareInLowerCase(row.serialNumber, queryFilter) || compareInLowerCase(row.message, queryFilter)) &&
            (alarmTypeFilter === 'all' ? true : row.alarmType === alarmTypeFilter),
        );
        setRows(filterResult);
      }, 500),
    [history],
  );
  function onQueryChange(e: ChangeEvent<HTMLInputElement>) {
    const { value } = e.target;
    setQuery(value);
    filterHistory(value, alarmType);
  }
  function onSelectChange(e: SelectChangeEvent) {
    const { value } = e.target;
    setAlarmType(value);
    filterHistory(query, value);
  }

  return (
    <SettingLayout>
      <Stack gap={3} sx={{ height: '100%' }}>
        <Typography variant="h5" fontWeight="bold">
          {t('Devices.label.alarmHistory')}
        </Typography>

        <Stack direction="row" gap={2}>
          <TextField
            value={query}
            placeholder={t('Common.label.search')}
            onChange={onQueryChange}
            slotProps={{
              input: {
                startAdornment: (
                  <InputAdornment position="start">
                    <Search />
                  </InputAdornment>
                ),
              },
            }}
            sx={{ width: '300px', bgcolor: 'white' }}
          />
          <DatePicker periodAtom={siteAlarmHistoryPeriodAtom} />
          <FormControl>
            <InputLabel>{t('Devices.label.alarmType')}</InputLabel>
            <Select
              label={t('Devices.label.alarmType')}
              value={alarmType}
              onChange={onSelectChange}
              sx={{ width: 160, bgcolor: 'white' }}
            >
              <MenuItem value="all">{t('Common.label.all')}</MenuItem>
              <MenuItem value="Temperature">{t('Devices.label.temperature')}</MenuItem>
              <MenuItem value="Humidity">{t('Devices.label.humidity')}</MenuItem>
              <MenuItem value="Battery">{t('Devices.label.battery')}</MenuItem>
              <MenuItem value="Offline">{t('Devices.label.offline')}</MenuItem>
            </Select>
          </FormControl>
        </Stack>

        <DataGrid
          disableColumnMenu
          disableColumnResize
          disableRowSelectionOnClick
          localeText={locale}
          columns={columns}
          rows={rows}
          initialState={{ pagination: { paginationModel: { pageSize: 25 } } }}
        />
      </Stack>
    </SettingLayout>
  );
}
